import firebase from "firebase/app";
import storage from "src/apiService/storage";

export async function deleteUploadedFile(filename: string) {
  const storageRef = storage().ref();
  const file = storageRef.child(filename);
  try {
    return await file.delete();
  } catch (e) {
    throw new Error(`Could not delete file: ${filename}`);
  }
}

export async function uploadFile(
  filename: string,
  content: any,
  { contentType = "application/json" }: { contentType?: string } = {}
) {
  const storageRef = storage().ref();
  const file = storageRef.child(filename);
  const task = await file.put(new Blob([content], { type: contentType }));

  if (task.state !== firebase.storage.TaskState.SUCCESS)
    throw new Error(`Couldnt upload file ${filename}: ${task.state}`);

  return true;
}

export async function getUploadedFile(filename: string) {
  const storageRef = storage().ref();
  return await storageRef.child(filename).getDownloadURL();
}

const imageBase64Cache = new Map<string, string | null>();

/** Cached base64, or undefined if this path has not been fetched yet. */
export function getCachedImageAsBase64(
  filename: string
): string | null | undefined {
  if (!imageBase64Cache.has(filename)) return undefined;
  return imageBase64Cache.get(filename)!;
}

export async function prefetchImageAsBase64(
  filename: string,
  { mimeType = "image/png" }: { mimeType?: string } = {}
): Promise<string | null> {
  const cached = getCachedImageAsBase64(filename);
  if (cached !== undefined) return cached;

  try {
    const base64 = await getImageAsBase64(filename, { mimeType });
    imageBase64Cache.set(filename, base64);
    return base64;
  } catch {
    imageBase64Cache.set(filename, null);
    return null;
  }
}

export async function getImageAsBase64(
  filename: string,
  { mimeType = "image/png" }: { mimeType?: string } = {}
) {
  const url = await getUploadedFile(filename);
  const response = await fetch(url);
  const blob = await response.blob();
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () =>
      resolve(
        (reader.result as string).replace(
          "data:application/json;",
          `data:${mimeType};`
        )
      );
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
